import { decodePassphrase, encodePassphrase } from '../client-utils';

export function getEncryptKeyFromLocationHash(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const raw = window.location.hash.slice(1);
  if (!raw) {
    return undefined;
  }
  try {
    return decodePassphrase(raw);
  } catch {
    return undefined;
  }
}

export function buildRoomPath(address: string, encryptKey?: string): string {
  const base = `/rooms/${address}`;
  if (!encryptKey) {
    return base;
  }
  return `${base}#${encodePassphrase(encryptKey)}`;
}

export function buildRoomAbsoluteUrl(
  origin: string,
  address: string,
  encryptKey?: string,
): string {
  return `${origin}${buildRoomPath(address, encryptKey)}`;
}

const ROOM_ADDRESS_RE = /^[a-zA-Z0-9][a-zA-Z0-9_-]{1,63}$/;

export type ParsedJoinRoom = {
  address: string;
  search?: string;
  hash?: string;
};

export function isRoomAddress(value: string): boolean {
  return ROOM_ADDRESS_RE.test(value);
}

export function parseJoinRoomInput(raw: string): ParsedJoinRoom | null {
  const value = raw.trim();
  if (!value) return null;

  const match = value.match(/(?:^|\/)rooms\/([^/?#]+)/i);
  if (match) {
    let address = match[1];
    try {
      address = decodeURIComponent(address);
    } catch {
      return null;
    }
    if (!isRoomAddress(address)) return null;

    const restStart = value.indexOf(match[1]) + match[1].length;
    const rest = value.slice(restStart);
    const hashIndex = rest.indexOf('#');
    const searchIndex = rest.indexOf('?');
    let search: string | undefined;
    let hash: string | undefined;
    if (searchIndex !== -1) {
      search = rest.slice(searchIndex, hashIndex === -1 ? undefined : hashIndex);
    }
    if (hashIndex !== -1) {
      hash = rest.slice(hashIndex);
    }
    return { address, search, hash };
  }

  if (isRoomAddress(value)) {
    return { address: value };
  }

  return null;
}

export function joinRoomHref(parsed: ParsedJoinRoom): string {
  return `/rooms/${encodeURIComponent(parsed.address)}${parsed.search ?? ''}${parsed.hash ?? ''}`;
}
